import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  Database,
  MessageCircle,
  RefreshCcw,
  Workflow,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../components/common/Theme/ThemeContext";
import AdvancedOverviewLineChart from "./AdvancedOverviewLineChart";

type Range = "24h" | "7d" | "30d";
type ChartView = "overview" | "orchestrations" | "copilots" | "compare";

const COLORS = {
  indigo: "#4F46E5",
  indigoSoft: "#818CF8",
  sky: "#0EA5E9",
  skySoft: "#7DD3FC",
  emerald: "#10B981",
  amber: "#F59E0B",
  rose: "#F43F5E",
  violet: "#8B5CF6",
};

type SeriesPoint = {
  t: string;
  runs: number;
  succeeded: number;
  failed: number;
  copilots: number;
  completed: number;
  blocked: number;
  avgLatency: number;
};

const runSeries: Record<Range, SeriesPoint[]> = {
  "24h": [
    { t: "12a", runs: 14, succeeded: 14, failed: 0, copilots: 22, completed: 21, blocked: 1, avgLatency: 1.8 },
    { t: "4a", runs: 8, succeeded: 8, failed: 0, copilots: 11, completed: 11, blocked: 0, avgLatency: 1.6 },
    { t: "8a", runs: 72, succeeded: 69, failed: 3, copilots: 98, completed: 94, blocked: 4, avgLatency: 2.4 },
    { t: "10a", runs: 96, succeeded: 91, failed: 5, copilots: 134, completed: 128, blocked: 6, avgLatency: 2.9 },
    { t: "12p", runs: 84, succeeded: 82, failed: 2, copilots: 118, completed: 113, blocked: 5, avgLatency: 2.6 },
    { t: "2p", runs: 108, succeeded: 102, failed: 6, copilots: 156, completed: 148, blocked: 8, avgLatency: 3.2 },
    { t: "4p", runs: 98, succeeded: 94, failed: 4, copilots: 142, completed: 136, blocked: 6, avgLatency: 2.8 },
    { t: "6p", runs: 58, succeeded: 57, failed: 1, copilots: 86, completed: 83, blocked: 3, avgLatency: 2.1 },
    { t: "8p", runs: 32, succeeded: 31, failed: 1, copilots: 48, completed: 46, blocked: 2, avgLatency: 1.9 },
    { t: "10p", runs: 18, succeeded: 18, failed: 0, copilots: 28, completed: 27, blocked: 1, avgLatency: 1.7 },
  ],
  "7d": [
    { t: "Mon", runs: 412, succeeded: 398, failed: 14, copilots: 842, completed: 812, blocked: 30, avgLatency: 2.4 },
    { t: "Tue", runs: 468, succeeded: 450, failed: 18, copilots: 918, completed: 886, blocked: 32, avgLatency: 2.6 },
    { t: "Wed", runs: 442, succeeded: 430, failed: 12, copilots: 886, completed: 858, blocked: 28, avgLatency: 2.3 },
    { t: "Thu", runs: 486, succeeded: 464, failed: 22, copilots: 964, completed: 922, blocked: 42, avgLatency: 2.9 },
    { t: "Fri", runs: 524, succeeded: 505, failed: 19, copilots: 1024, completed: 986, blocked: 38, avgLatency: 2.7 },
    { t: "Sat", runs: 218, succeeded: 214, failed: 4, copilots: 486, completed: 472, blocked: 14, avgLatency: 1.9 },
    { t: "Sun", runs: 242, succeeded: 236, failed: 6, copilots: 524, completed: 508, blocked: 16, avgLatency: 2.0 },
  ],
  "30d": [
    { t: "W1", runs: 2480, succeeded: 2412, failed: 68, copilots: 5240, completed: 5088, blocked: 152, avgLatency: 2.5 },
    { t: "W2", runs: 2720, succeeded: 2638, failed: 82, copilots: 5680, completed: 5496, blocked: 184, avgLatency: 2.7 },
    { t: "W3", runs: 2940, succeeded: 2866, failed: 74, copilots: 6120, completed: 5942, blocked: 178, avgLatency: 2.6 },
    { t: "W4", runs: 3180, succeeded: 3089, failed: 91, copilots: 6480, completed: 6264, blocked: 216, avgLatency: 2.8 },
  ],
};

const chartViews: { id: ChartView; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "orchestrations", label: "Orchestrations" },
  { id: "copilots", label: "Copilots" },
  { id: "compare", label: "Compare" },
];

const featureCards = [
  {
    key: "orch",
    label: "Agent Orchestrations",
    value: "24",
    delta: 8.2,
    up: true,
    note: "3 running now",
    icon: Workflow,
    to: "/portal/workflows",
    tint: "bg-indigo-50 text-indigo-600",
  },
  {
    key: "copilot",
    label: "Copilots",
    value: "47",
    delta: 12.4,
    up: true,
    note: "1,284 sessions today",
    icon: MessageCircle,
    to: "/portal/chatagent",
    tint: "bg-sky-50 text-sky-600",
  },
  {
    key: "models",
    label: "Data Models",
    value: "18",
    delta: 2.1,
    up: false,
    note: "2 need attention",
    icon: Database,
    to: "/portal/configurations/data-ingestion",
    tint: "bg-emerald-50 text-emerald-600",
  },
  {
    key: "kb",
    label: "Knowledge Workspace",
    value: "12",
    delta: 4.0,
    up: true,
    note: "15.8k docs indexed",
    icon: BookOpen,
    to: "/portal/knowledge-base",
    tint: "bg-violet-50 text-violet-600",
  },
];

const resources = [
  { name: "LLM Routing", status: "Healthy", latency: "142ms", ok: true },
  { name: "Agent Orchestrations", status: "Degraded", latency: "312ms", ok: false },
  { name: "Knowledge Workspace", status: "Degraded", latency: "284ms", ok: false },
  { name: "Data Models", status: "Healthy", latency: "68ms", ok: true },
  { name: "MCP Resources", status: "Healthy", latency: "96ms", ok: true },
  { name: "Schedules", status: "Healthy", latency: "38ms", ok: true },
];

const alerts = [
  {
    level: "Critical",
    title: "Contracts Store sync failed",
    detail: "Data Models · no successful sync for 3h",
    to: "/portal/configurations/data-ingestion",
    color: "bg-rose-500",
  },
  {
    level: "High",
    title: "Credit Risk workflow failing",
    detail: "Orchestrations · 5.2% error rate today",
    to: "/portal/workflows",
    color: "bg-orange-500",
  },
  {
    level: "Warning",
    title: "HR Knowledge Base behind",
    detail: "142 documents pending ingest",
    to: "/portal/knowledge-base",
    color: "bg-amber-500",
  },
  {
    level: "Warning",
    title: "Copilot latency elevated",
    detail: "Customer Support · p95 6.2s",
    to: "/portal/chatagent",
    color: "bg-amber-500",
  },
];

const activity = [
  { name: "Credit Risk Workflow", kind: "Orchestration", event: "Failed", time: "3m", bad: true },
  { name: "Finance Copilot", kind: "Copilot", event: "Completed", time: "5m", bad: false },
  { name: "Customer CRM", kind: "Data Model", event: "Synced", time: "8m", bad: false },
  { name: "HR Policies", kind: "Knowledge", event: "Delayed", time: "28m", bad: true },
  { name: "Legal Reviewer", kind: "Copilot", event: "Blocked", time: "41m", bad: true },
];

const knowledgePie = [
  { name: "Finance KB", value: 4820, color: COLORS.indigo },
  { name: "Product Docs", value: 3940, color: COLORS.sky },
  { name: "Legal Corpus", value: 2860, color: COLORS.emerald },
  { name: "HR Policies", value: 2180, color: COLORS.amber },
  { name: "Support FAQ", value: 2040, color: COLORS.violet },
];

const knowledgePieTotal = knowledgePie.reduce((s, d) => s + d.value, 0);

function fmt(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function Tip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length || !label) return null;
  return (
    <div className="rounded-xl bg-white px-3.5 py-2.5 text-xs text-neutral-800 shadow-lg ring-1 ring-neutral-200">
      <p className="mb-1.5 font-medium text-neutral-500">{label}</p>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.name} className="flex items-center justify-between gap-5">
            <span className="flex items-center gap-1.5 text-neutral-600">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
              {p.name}
            </span>
            <span className="font-semibold tabular-nums">
              {typeof p.value === "number" && p.name.toLowerCase().includes("latency")
                ? `${p.value.toFixed(1)}s`
                : Number(p.value).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState<Range>("7d");
  const [chartView, setChartView] = useState<ChartView>("overview");
  const { theme } = useTheme();
  const isLight = theme === "white";

  const data = useMemo(() => runSeries[range], [range]);
  const totals = useMemo(() => {
    const runs = data.reduce((s, d) => s + d.runs, 0);
    const failed = data.reduce((s, d) => s + d.failed, 0);
    const succeeded = data.reduce((s, d) => s + d.succeeded, 0);
    const copilots = data.reduce((s, d) => s + d.copilots, 0);
    const completed = data.reduce((s, d) => s + d.completed, 0);
    const blocked = data.reduce((s, d) => s + d.blocked, 0);
    const avgLatency = data.reduce((s, d) => s + d.avgLatency, 0) / Math.max(data.length, 1);
    return {
      runs,
      failed,
      succeeded,
      copilots,
      completed,
      blocked,
      avgLatency,
      success: runs ? (succeeded / runs) * 100 : 100,
    };
  }, [data]);

  const overviewPeak = useMemo(() => {
    if (!data.length) return null;
    return data.reduce((best, d) => (d.runs + d.copilots > best.runs + best.copilots ? d : best), data[0]);
  }, [data]);

  /* Pure white surfaces in white theme */
  const page = isLight ? "bg-white text-neutral-900" : "bg-zinc-950 text-zinc-100";
  const card = isLight
    ? "bg-white border border-neutral-200"
    : "bg-zinc-900 border border-zinc-800";
  const muted = isLight ? "text-neutral-500" : "text-zinc-500";
  const soft = isLight ? "text-neutral-600" : "text-zinc-400";
  const title = isLight ? "text-neutral-900" : "text-white";
  const hair = isLight ? "border-neutral-100" : "border-zinc-800";
  const grid = isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)";
  const tick = isLight ? "#A3A3A3" : "#71717A";
  const chipIdle = isLight ? "text-neutral-600 hover:bg-neutral-50" : "text-zinc-400 hover:bg-zinc-800";
  const chipOn = isLight ? "bg-neutral-900 text-white" : "bg-white text-zinc-900";
  const rowHover = isLight ? "hover:bg-neutral-50" : "hover:bg-zinc-800/60";
  const viewIdle = isLight
    ? "text-neutral-600 hover:bg-white hover:text-neutral-900"
    : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100";
  const viewOn = isLight
    ? "bg-white text-neutral-900 shadow-sm ring-1 ring-neutral-200"
    : "bg-zinc-800 text-white shadow-sm ring-1 ring-zinc-700";

  const legendForView: Record<ChartView, { color: string; label: string }[]> = {
    overview: [
      { color: COLORS.indigo, label: "Orchestration runs" },
      { color: COLORS.sky, label: "Copilot sessions" },
    ],
    orchestrations: [
      { color: COLORS.indigo, label: "Succeeded" },
      { color: COLORS.rose, label: "Failed" },
    ],
    copilots: [
      { color: COLORS.sky, label: "Completed" },
      { color: COLORS.amber, label: "Blocked" },
    ],
    compare: [
      { color: COLORS.indigo, label: "Orchestration runs" },
      { color: COLORS.sky, label: "Copilot sessions" },
    ],
  };

  return (
    <div className={`min-h-full ${page}`}>
      <div className="mx-auto w-full max-w-[1280px] space-y-6 p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-[1.625rem] font-semibold tracking-tight ${title}`}>Dashboard</h1>
            <p className={`mt-1 text-sm ${muted}`}>
              Orchestrations, copilots, data models, and knowledge health
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex rounded-xl p-1 ${isLight ? "bg-neutral-50 border border-neutral-200" : "bg-zinc-900 border border-zinc-800"}`}>
              {(["24h", "7d", "30d"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    range === r ? chipOn : chipIdle
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${card} ${soft}`}
              aria-label="Refresh"
            >
              <RefreshCcw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Feature KPIs */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
          {featureCards.map((f) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.key}
                to={f.to}
                className={`group rounded-2xl p-4 transition-colors ${isLight ? "hover:border-neutral-300" : "hover:border-zinc-700"} ${card}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${isLight ? f.tint : "bg-zinc-800 text-zinc-300"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span
                    className={`inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${
                      f.up ? "text-emerald-600" : "text-rose-500"
                    }`}
                  >
                    {f.up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                    {f.delta}%
                  </span>
                </div>
                <p className={`mt-4 text-xs font-medium ${muted}`}>{f.label}</p>
                <p className={`mt-1 text-2xl font-semibold tracking-tight tabular-nums ${title}`}>{f.value}</p>
                <p className={`mt-1 text-xs ${muted}`}>{f.note}</p>
              </Link>
            );
          })}
        </div>

        {/* Main chart + side summary */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className={`rounded-2xl p-5 xl:col-span-2 ${card}`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className={`text-sm font-semibold ${title}`}>Orchestration & copilot activity</h2>
                <p className={`mt-0.5 text-xs ${muted}`}>Workflow runs and copilot sessions · {range}</p>
              </div>
              <div
                className={`flex flex-wrap rounded-xl p-1 ${isLight ? "bg-neutral-50" : "bg-zinc-950/60"}`}
              >
                {chartViews.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setChartView(v.id)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                      chartView === v.id ? viewOn : viewIdle
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap gap-6">
                {chartView === "overview" ? (
                  <>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Orchestration runs</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>{fmt(totals.runs)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Copilot sessions</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>{fmt(totals.copilots)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Success rate</p>
                      <p className="text-xl font-semibold tabular-nums text-emerald-600">
                        {totals.success.toFixed(1)}%
                      </p>
                    </div>
                  </>
                ) : null}
                {chartView === "orchestrations" ? (
                  <>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Orchestration runs</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>{fmt(totals.runs)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Success rate</p>
                      <p className="text-xl font-semibold tabular-nums text-emerald-600">
                        {totals.success.toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Failed</p>
                      <p className="text-xl font-semibold tabular-nums text-rose-500">{fmt(totals.failed)}</p>
                    </div>
                  </>
                ) : null}
                {chartView === "copilots" ? (
                  <>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Copilot sessions</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>{fmt(totals.copilots)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Completed</p>
                      <p className="text-xl font-semibold tabular-nums text-sky-600">{fmt(totals.completed)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Avg latency</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>
                        {totals.avgLatency.toFixed(1)}s
                      </p>
                    </div>
                  </>
                ) : null}
                {chartView === "compare" ? (
                  <>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Orchestration runs</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>{fmt(totals.runs)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Copilot sessions</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>{fmt(totals.copilots)}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${muted}`}>Sessions / run</p>
                      <p className={`text-xl font-semibold tabular-nums ${title}`}>
                        {(totals.copilots / Math.max(totals.runs, 1)).toFixed(1)}
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-[11px]">
                {legendForView[chartView].map((l) => (
                  <span key={l.label} className={`inline-flex items-center gap-1.5 ${muted}`}>
                    <span
                      className={
                        chartView === "overview"
                          ? "h-0.5 w-3.5 rounded-full"
                          : "h-2 w-2 rounded-sm"
                      }
                      style={{ background: l.color }}
                    />
                    {l.label}
                  </span>
                ))}
              </div>
            </div>

            {chartView === "overview" ? (
              <p className={`mb-3 text-[11px] ${muted}`}>
                Left axis: orchestration runs · Right axis: copilot sessions
                {overviewPeak ? (
                  <>
                    {" "}
                    · Busiest: <span className={`font-medium ${soft}`}>{overviewPeak.t}</span>
                  </>
                ) : null}
              </p>
            ) : null}

            {chartView === "overview" ? (
              <AdvancedOverviewLineChart
                data={data}
                isLight={isLight}
                height={300}
                runsColor={COLORS.indigo}
                sessionsColor={COLORS.sky}
              />
            ) : (
            <ResponsiveContainer width="100%" height={280}>
              {chartView === "orchestrations" ? (
                <BarChart key="orchestrations" data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} barGap={4} barCategoryGap="22%">
                  <defs>
                    <linearGradient id="succBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.indigoSoft} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.indigo} stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="failBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FB7185" stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.rose} stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={grid} />
                  <XAxis dataKey="t" axisLine={false} tickLine={false} tick={{ fill: tick, fontSize: 11 }} dy={6} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    tick={{ fill: tick, fontSize: 11 }}
                    tickFormatter={(v: number) => fmt(v)}
                  />
                  <Tooltip content={<Tip />} cursor={{ fill: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="succeeded" name="Succeeded" fill="url(#succBar)" radius={[6, 6, 2, 2]} maxBarSize={28} />
                  <Bar dataKey="failed" name="Failed" fill="url(#failBar)" radius={[6, 6, 2, 2]} maxBarSize={28} />
                </BarChart>
              ) : chartView === "copilots" ? (
                <ComposedChart key="copilots" data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="doneBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.skySoft} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.sky} stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={grid} />
                  <XAxis dataKey="t" axisLine={false} tickLine={false} tick={{ fill: tick, fontSize: 11 }} dy={6} />
                  <YAxis
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    tick={{ fill: tick, fontSize: 11 }}
                    tickFormatter={(v: number) => fmt(v)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    width={36}
                    tick={{ fill: tick, fontSize: 11 }}
                    tickFormatter={(v: number) => `${v}s`}
                  />
                  <Tooltip content={<Tip />} cursor={{ fill: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }} />
                  <Bar
                    yAxisId="left"
                    dataKey="completed"
                    name="Completed"
                    stackId="copilot"
                    fill="url(#doneBar)"
                    radius={[0, 0, 0, 0]}
                    maxBarSize={36}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="blocked"
                    name="Blocked"
                    stackId="copilot"
                    fill={COLORS.amber}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={36}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgLatency"
                    name="Avg latency"
                    stroke={COLORS.violet}
                    strokeWidth={2}
                    dot={{ r: 3, fill: COLORS.violet, strokeWidth: 0 }}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                </ComposedChart>
              ) : (
                <BarChart key="compare" data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} barGap={6} barCategoryGap="20%">
                  <defs>
                    <linearGradient id="orchCmp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.indigoSoft} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.indigo} stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="copCmp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.skySoft} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.sky} stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={grid} />
                  <XAxis dataKey="t" axisLine={false} tickLine={false} tick={{ fill: tick, fontSize: 11 }} dy={6} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    tick={{ fill: tick, fontSize: 11 }}
                    tickFormatter={(v: number) => fmt(v)}
                  />
                  <Tooltip content={<Tip />} cursor={{ fill: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="runs" name="Orchestration runs" fill="url(#orchCmp)" radius={[6, 6, 2, 2]} maxBarSize={32}>
                    {data.map((entry) => (
                      <Cell key={`orch-${entry.t}`} fillOpacity={entry.failed > 15 ? 0.75 : 1} />
                    ))}
                  </Bar>
                  <Bar dataKey="copilots" name="Copilot sessions" fill="url(#copCmp)" radius={[6, 6, 2, 2]} maxBarSize={32} />
                </BarChart>
              )}
            </ResponsiveContainer>
            )}
          </div>

          {/* Knowledge + models snapshot */}
          <div className={`flex flex-col rounded-2xl p-5 ${card}`}>
            <h2 className={`text-sm font-semibold ${title}`}>Data & knowledge</h2>
            <p className={`mt-0.5 text-xs ${muted}`}>Indexed documents by workspace</p>

            <div className="relative mt-2 flex-1">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={knowledgePie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={82}
                    paddingAngle={3}
                    stroke={isLight ? "#fff" : "#18181b"}
                    strokeWidth={3}
                  >
                    {knowledgePie.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      `${Number(value).toLocaleString()} docs · ${((Number(value) / knowledgePieTotal) * 100).toFixed(0)}%`,
                      name,
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border: isLight ? "1px solid #e5e5e5" : "1px solid #3f3f46",
                      background: isLight ? "#fff" : "#18181b",
                      color: isLight ? "#171717" : "#f4f4f5",
                      fontSize: 12,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <p className={`text-[10px] font-medium uppercase tracking-wide ${muted}`}>Indexed</p>
                <p className={`text-xl font-semibold tabular-nums ${title}`}>
                  {(knowledgePieTotal / 1000).toFixed(1)}k
                </p>
              </div>
            </div>

            <ul className="mt-1 space-y-2">
              {knowledgePie.map((row) => {
                const pct = Math.round((row.value / knowledgePieTotal) * 100);
                return (
                  <li key={row.name} className="flex items-center justify-between gap-3 text-xs">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: row.color }} />
                      <span className={`truncate font-medium ${title}`}>{row.name}</span>
                    </span>
                    <span className={`shrink-0 tabular-nums ${muted}`}>
                      {fmt(row.value)} · {pct}%
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className={`mt-5 grid grid-cols-2 gap-3 border-t pt-4 ${hair}`}>
              <div>
                <p className={`text-[11px] ${muted}`}>Models healthy</p>
                <p className={`text-lg font-semibold tabular-nums ${title}`}>16 / 18</p>
              </div>
              <div>
                <p className={`text-[11px] ${muted}`}>Coverage</p>
                <p className={`text-lg font-semibold tabular-nums ${title}`}>94%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Health + Alerts + Activity */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className={`rounded-2xl p-5 ${card}`}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className={`text-sm font-semibold ${title}`}>Resource health</h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                4 / 6 ok
              </span>
            </div>
            <ul className="space-y-1">
              {resources.map((r) => (
                <li
                  key={r.name}
                  className={`flex items-center justify-between gap-3 rounded-xl px-2 py-2.5 ${rowHover}`}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${r.ok ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <span className={`truncate text-sm font-medium ${title}`}>{r.name}</span>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={`text-[11px] font-medium ${r.ok ? "text-emerald-600" : "text-amber-600"}`}>
                      {r.status}
                    </p>
                    <p className={`text-[10px] tabular-nums ${muted}`}>{r.latency}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className={`rounded-2xl p-5 ${card}`}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className={`text-sm font-semibold ${title}`}>Alerts & warnings</h2>
              <Link to="/portal/logs" className="text-[11px] font-medium text-indigo-600 hover:text-indigo-500">
                View all
              </Link>
            </div>
            <ul className="space-y-3">
              {alerts.map((a) => (
                <li key={a.title}>
                  <Link
                    to={a.to}
                    className={`flex gap-3 rounded-xl p-2.5 transition-colors ${rowHover}`}
                  >
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm font-medium ${title}`}>{a.title}</p>
                        <ArrowRight className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
                      </div>
                      <p className={`mt-0.5 text-xs ${muted}`}>
                        <span className={`font-medium ${muted}`}>{a.level}</span>
                        {" · "}
                        {a.detail}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className={`rounded-2xl p-5 ${card}`}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className={`text-sm font-semibold ${title}`}>Recent activity</h2>
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            </div>
            <ul className={`divide-y ${hair}`}>
              {activity.map((row) => (
                <li key={`${row.name}-${row.time}`} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm font-medium ${title}`}>{row.name}</p>
                    <p className={`text-xs ${muted}`}>
                      {row.kind} · {row.event}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {row.bad ? (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                    <span className={`text-[11px] tabular-nums ${muted}`}>{row.time}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
